import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import ValidateSOF from '../components/ValidateSOF';
import SOXCycleMappingReport from '../components/SOXCycleMappingReport';
import Subscriptions from '../components/Subscriptions';
import CreateSubscription from '../components/CreateSubscription';
import ComplianceReport from '../components/ComplianceReport';
import SOXDashboard from '../components/SOXDashboard';
import Materiality from '../components/Materiality';
import MaterialityDashboard from '../components/MaterialityDashboard';
import SOXFlowcharts from '../components/SOXFlowcharts';
import SOXControlLibrary from '../components/SOXControlLibrary';
import ControlCreationWizard from '../components/ControlCreationWizard';
import TestStepGeneration from '../components/TestStepGeneration';
import DataSourceConfiguration from '../components/DataSourceConfiguration';
import ChatInterface from '../components/ChatInterface';
import Controls from '../components/Controls';
import Risks from '../components/Risks';
import RiskDetailsPage from '../features/risks/pages/RiskDetailsPage.tsx';
import { LegacySoxRiskEditRoute } from '../features/risks/pages/LegacySoxRiskEditRoute.tsx';
import Systems from '../components/Systems';
import SystemCreate from '../components/SystemCreate';
import SystemDetail from '../components/SystemDetail';
import Reports from '../components/Reports';
import ReportCreate from '../components/ReportCreate';
import ReportDetail from '../components/ReportDetail';
import NewControl from '../components/NewControl';
import ViewControl from '../components/ViewControl';
import RunTestFlow from '../components/RunTestFlow';
import AttributeResultPage from '../components/attribute-result/AttributeResultPage';
import AIEngagementPage from '../components/ai-engagement/AIEngagementPage';
import ProcessFlowchartView from '../components/ProcessFlowchartView';
import ProcessesScreen from '../components/ProcessesScreen';
import ProcessCreatePage from '../components/ProcessCreatePage';
import ProcessUploadPage from '../components/ProcessUploadPage';
import CycleEndToEndFlowchartPage from '../components/merge-flowchart/CycleEndToEndFlowchartPage';
import CycleCombinedFlowchartPage from '../components/merge-flowchart/CycleCombinedFlowchartPage';
import IssuesManagement from '../components/IssuesManagement';
import KeyedIssueDetail from '../components/KeyedIssueDetail.tsx';
import RemediationExecution from '../components/RemediationExecution';
import { TENANT_PATHS } from './paths.ts';
import { TestRunsPage } from '../features/control-testing/pages/TestRunsPage.tsx';
import TranscriptionPage from '../features/transcription/pages/TranscriptionPage.tsx';
import SopListPage from '../features/sop-synthesizer/pages/SopListPage.tsx';
import SopNewPage from '../features/sop-synthesizer/pages/SopNewPage.tsx';
import SopDetailPage from '../features/sop-synthesizer/pages/SopDetailPage.tsx';
import { ControllerDashboard } from '../components/analytics/controller/ControllerDashboard.tsx';
import { CAEDashboard } from '../components/analytics/cae/CAEDashboard.tsx';
import { CFODashboard } from '../components/analytics/cfo/CFODashboard.tsx';
import { TesterDashboard } from '../components/analytics/tester/TesterDashboard.tsx';
import { ExternalAuditorDashboard } from '../components/analytics/auditor/ExternalAuditorDashboard.tsx';
import { ReviewerDashboard } from '../components/analytics/reviewer/ReviewerDashboard.tsx';
import { PBCTracker } from '../components/pbc/PBCTracker.tsx';
import { SelfServeDashboard } from '../components/analytics/SelfServeDashboard.tsx';
import AuditYearSettings from '../pages/AuditYearSettings.tsx';
import OrgCreateUserPage from '../pages/OrgCreateUserPage.tsx';
import OrgAdminPage from '../pages/OrgAdminPage.tsx';
import OrgAdminRoute from '../components/OrgAdminRoute.tsx';
import OrgAuditLogPage from '../pages/OrgAuditLogPage.tsx';
import AIActivity from '../pages/AIActivity.tsx';

const SubscriptionsPage: any = Subscriptions;
const CreateSubscriptionPage: any = CreateSubscription;

interface TenantRoutesProps {
  user: unknown;
}

export function renderTenantRoutes({ user }: TenantRoutesProps): React.ReactNode {
  return (
    <>
      <Route
        path={TENANT_PATHS.SOX_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <SOXDashboard user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_PROCESS}
        element={
          <ProtectedRoute user={user}>
            <ProcessFlowchartView />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_PROCESSES}
        element={
          <ProtectedRoute user={user}>
            <ProcessesScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_PROCESS_EDIT}
        element={
          <ProtectedRoute user={user}>
            <ProcessCreatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_PROCESS_CREATE}
        element={
          <ProtectedRoute user={user}>
            <ProcessCreatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_PROCESS_UPLOAD}
        element={
          <ProtectedRoute user={user}>
            <ProcessUploadPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CYCLE_END_TO_END}
        element={
          <ProtectedRoute user={user}>
            <CycleEndToEndFlowchartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CYCLE_COMBINED_FLOWCHART}
        element={
          <ProtectedRoute user={user}>
            <CycleCombinedFlowchartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.COMPLIANCE_REPORT}
        element={
          <ProtectedRoute user={user}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ComplianceReport user={user} />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.MATERIALITY_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <MaterialityDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.MATERIALITY}
        element={
          <ProtectedRoute user={user}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <Materiality user={user} />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.RISK_ASSESSMENT}
        element={
          <Navigate to={TENANT_PATHS.MATERIALITY} replace />
        }
      />
      <Route
        path={TENANT_PATHS.SOX_FLOWCHARTS}
        element={
          <ProtectedRoute user={user}>
            <SOXFlowcharts />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROL_LIBRARY}
        element={
          <ProtectedRoute user={user}>
            <SOXControlLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_RISKS}
        element={
          <ProtectedRoute user={user}>
            <Risks />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_RISKS_CREATE}
        element={
          <ProtectedRoute user={user}>
            <RiskDetailsPage mode="create" />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_RISKS_VIEW}
        element={
          <ProtectedRoute user={user}>
            <RiskDetailsPage mode="view" />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_RISKS_EDIT}
        element={
          <ProtectedRoute user={user}>
            <LegacySoxRiskEditRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS}
        element={
          <ProtectedRoute user={user}>
            <Controls />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_SYSTEMS}
        element={
          <ProtectedRoute user={user}>
            <Systems />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_SYSTEMS_NEW}
        element={
          <ProtectedRoute user={user}>
            <SystemCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_SYSTEMS_DETAIL}
        element={
          <ProtectedRoute user={user}>
            <SystemDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_REPORTS}
        element={
          <ProtectedRoute user={user}>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_REPORTS_NEW}
        element={
          <ProtectedRoute user={user}>
            <ReportCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_REPORTS_DETAIL}
        element={
          <ProtectedRoute user={user}>
            <ReportDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_NEW}
        element={
          <ProtectedRoute user={user}>
            <NewControl />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_WIZARD}
        element={
          <ProtectedRoute user={user}>
            <ControlCreationWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_WIZARD_TEST_STEPS}
        element={
          <ProtectedRoute user={user}>
            <TestStepGeneration />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_WIZARD_DATA_SOURCE}
        element={
          <ProtectedRoute user={user}>
            <DataSourceConfiguration />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_REFINE}
        element={
          <ProtectedRoute user={user}>
            <ChatInterface />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_VIEW}
        element={
          <ProtectedRoute user={user}>
            <ViewControl />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_RUN_TEST}
        element={
          <ProtectedRoute user={user}>
            <RunTestFlow />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_ATTRIBUTE_RESULT}
        element={
          <ProtectedRoute user={user}>
            <AttributeResultPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_CONTROLS_AI_ENGAGEMENT}
        element={
          <ProtectedRoute user={user}>
            <AIEngagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOX_MAPPING_REPORT}
        element={
          <ProtectedRoute user={user}>
            <SOXCycleMappingReport user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.VALIDATE}
        element={
          <ProtectedRoute user={user}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ValidateSOF user={user} />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.CONTROL_TESTING_TEST_RUNS}
        element={
          <ProtectedRoute user={user}>
            <div className="min-h-screen bg-gray-50 pb-12">
              <div className="max-w-7xl mx-auto px-8 py-8">
                <TestRunsPage />
              </div>
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.ISSUES_MANAGEMENT}
        element={
          <ProtectedRoute user={user}>
            <IssuesManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.ISSUE_DETAIL}
        element={
          <ProtectedRoute user={user}>
            <KeyedIssueDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.REMEDIATION_EXECUTION}
        element={
          <ProtectedRoute user={user}>
            <RemediationExecution />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SUBSCRIPTIONS}
        element={
          <ProtectedRoute user={user}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="bg-white rounded-lg shadow p-6">
                <SubscriptionsPage />
              </div>
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.CREATE_SUBSCRIPTION}
        element={
          <ProtectedRoute user={user}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="bg-white rounded-lg shadow p-6">
                <CreateSubscriptionPage />
              </div>
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.TRANSCRIPTION}
        element={
          <ProtectedRoute user={user}>
            <TranscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOP_SYNTHESIZER}
        element={
          <ProtectedRoute user={user}>
            <SopListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOP_SYNTHESIZER_NEW}
        element={
          <ProtectedRoute user={user}>
            <SopNewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.SOP_SYNTHESIZER_DETAIL}
        element={
          <ProtectedRoute user={user}>
            <SopDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.CONTROLLER_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <ControllerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.CAE_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <CAEDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.CFO_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <CFODashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.TESTER_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <TesterDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.AUDITOR_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <ExternalAuditorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.REVIEWER_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <ReviewerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.PBC_TRACKER}
        element={
          <ProtectedRoute user={user}>
            <PBCTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path={TENANT_PATHS.MY_DASHBOARD}
        element={
          <ProtectedRoute user={user}>
            <SelfServeDashboard />
          </ProtectedRoute>
        }
      />
      {/* Audit Year Settings — org_admin only */}
      <Route
        path={TENANT_PATHS.SETTINGS_AUDIT_YEARS}
        element={
          <ProtectedRoute user={user}>
            <OrgAdminRoute>
              <AuditYearSettings />
            </OrgAdminRoute>
          </ProtectedRoute>
        }
      />
      {/* Org Admin — create user (org_admin only) */}
      <Route
        path={TENANT_PATHS.SETTINGS_ORG_USERS}
        element={
          <ProtectedRoute user={user}>
            <OrgAdminRoute>
              <OrgCreateUserPage />
            </OrgAdminRoute>
          </ProtectedRoute>
        }
      />
      {/* Org Admin — manage members (org_admin only) */}
      <Route
        path={TENANT_PATHS.SETTINGS_ORG_MEMBERS}
        element={
          <ProtectedRoute user={user}>
            <OrgAdminRoute>
              <OrgAdminPage />
            </OrgAdminRoute>
          </ProtectedRoute>
        }
      />
      {/* Org Admin — audit log (org_admin only, current org) */}
      <Route
        path={TENANT_PATHS.SETTINGS_ORG_AUDIT_LOG}
        element={
          <ProtectedRoute user={user}>
            <OrgAdminRoute>
              <OrgAuditLogPage />
            </OrgAdminRoute>
          </ProtectedRoute>
        }
      />
      {/* AI Activity — org_admin only, and only when org has AI integration */}
      <Route
        path={TENANT_PATHS.AI_ACTIVITY}
        element={
          <ProtectedRoute user={user}>
            <OrgAdminRoute requireOwnAIIntegration>
              <AIActivity />
            </OrgAdminRoute>
          </ProtectedRoute>
        }
      />
    </>
  );
}
